import {
  Box,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Link,
} from '@chakra-ui/react';
import { sectionListSx } from '../../utils/sectionListSx';

export const TermsOfExchangePage = () => {
  return (
    <Box
      bg="white"
      color="gray.800"
      maxW="756px"
      mx="auto"
      px={6}
      pt={16}
      pb={24}
      fontFamily="marketingPage"
      fontSize="15px"
      lineHeight="24px">
      <VStack align="start" spacing={16}>
        {/* Header */}
        <Box>
          <Heading as="h1" fontSize="40px" mb={4} fontFamily="marketingPage">
            Exchange of Data Processing Services and Data Transfer
          </Heading>
          <Text fontSize="sm" color="gray.600">
            Effective Date: 2026-01-01
          </Text>
          <Text fontSize="sm" color="gray.600">
            Last Updated: 2026-01-01
          </Text>
        </Box>

        {/* Section 1 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            1. General
          </Heading>
          <Text>
            Customer has the right, by written notice (
            <strong>Switching Notice</strong>), to switch from the data
            processing services provided by Merkatus to the data processing
            services of another service provider or to transfer all transferable
            data and digital assets described in Section 2 to ICT infrastructure
            located in Customer’s own premises (jointly
            <strong> Data Transfer</strong>). As Merkatus’ data processing
            services are cloud services and are based on its or third parties’
            intellectual property rights, the parties acknowledge that the
            applications described in Regulation (EU) 2023/2854 (
            <strong>Data Act</strong>) will not be transferred as part of the
            Data Transfer, unless otherwise agreed or required by mandatory
            legislation.
          </Text>
        </Box>

        {/* Section 2 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            2. Subject of Data Transfer
          </Heading>
          <Text>
            The list of data and digital assets subject to the Data Transfer (
            <strong>Transferable Data</strong>) is available at{' '}
            <Link
              href="https://merkatus.io/transferable-data"
              color="darkBlue.900"
              isExternal>
              https://merkatus.io/transferable-data
            </Link>
            . The following categories of data, which are Merkatus’ trade
            secrets, are not included in the Transferable Data: list available
            at{' '}
            <Link
              href="https://merkatus.io/excluded-data"
              color="darkBlue.900"
              isExternal>
              https://merkatus.io/excluded-data
            </Link>
            .
          </Text>
        </Box>

        {/* Section 3 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            3. Notice Period
          </Heading>
          <Text>
            A 2-month notice period (<strong>Notice Period</strong>) shall
            commence upon Customer’s submission of a Switching Notice. If
            Customer has only given notice of termination, Customer may also
            give a Switching Notice during the Notice Period, but this will not
            extend the original Notice Period.
          </Text>
        </Box>

        {/* Section 4 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            4. Customer’s Obligations
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(4)}>
            <ListItem>
              Customer undertakes to take all reasonable measures to ensure the
              effective Data Transfer and is responsible for its own actions and
              the accuracy of the information it provides. Customer is
              responsible for the use and suitability of the Transferable Data
              for its own business purposes or those of third parties authorised
              by it.
            </ListItem>
            <ListItem>
              In the Switching Notice, Customer may notify of its intention to
              carry out one or more of the following actions with Merkatus’
              assistance after the Notice Period has expired: (a) switch to
              another data processing service provider, in which case Customer
              must provide Merkatus with all necessary information about the new
              service provider and other required information; (b) carry out the
              Data Transfer described in Section 1 to its own ICT infrastructure
              located at its premises; or (c) delete the Transferable Data.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 5 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            5. Merkatus’ Obligations
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(5)}>
            <ListItem>
              Merkatus shall commence the Data Transfer without undue delay
              after receiving Customer’s Switching Notice and shall complete the
              Data Transfer within 30 days of the end of the Notice Period (
              <strong>Transition Period</strong>). If the Data Transfer cannot
              be carried out during the Transition Period for technical reasons,
              Merkatus shall notify Customer of an alternative transition period
              within 14 days of the Switching Notice. Without limiting Merkatus’
              right to extend the Transition Period, Customer shall have a
              one-time right to extend the Transition Period so that its total
              duration does not exceed 7 months.
            </ListItem>
            <ListItem>
              During the Notice Period, Merkatus shall: (a) provide Customer or
              third parties authorised by Customer with reasonable assistance in
              the Data Transfer; (b) exercise due care to maintain business
              continuity and continue to provide the relevant functions or
              services in accordance with the Agreement; (c) provide clear
              information on known risks regarding the continuity of its
              functions or the provision of its services; and (d) ensure that
              security is maintained during the Data Transfer, with particular
              regard to the ongoing security of the data during the Retrieval
              Period as defined in Section 6 in accordance with applicable law.
              Merkatus’ data security practices shall be followed.
            </ListItem>
            <ListItem>
              Merkatus shall support Customer’s exit strategy from the services
              and provide Customer with the information reasonably necessary for
              planning and implementing the exit.
            </ListItem>
            <ListItem>
              After the Data Transfer, Merkatus shall not be responsible for the
              integrity of the data or for Customer or a third party authorised
              by Customer being able to utilise the Transferable Data after the
              transfer.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 6 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            6. Retrieval Period
          </Heading>
          <Text>
            Merkatus shall keep the Transferable Data available for retrieval by
            Customer for 30 days after the end of the Transition Period (
            <strong>Retrieval Period</strong>).
          </Text>
        </Box>

        {/* Section 7 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            7. Deletion of Transferable Data
          </Heading>
          <Text>
            Merkatus shall delete all Transferable Data from its own systems and
            backups at the end of the Retrieval Period or, if the parties have
            agreed in advance on a longer Retrieval Period, at the end of this
            extended Retrieval Period, provided that the Data Transfer has been
            successfully completed. However, Merkatus shall not delete data that
            cannot be deleted under applicable law or that has not been
            generated as a result of Customer’s use of the service (e.g.
            statistics).
          </Text>
        </Box>

        {/* Section 8 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            8. Termination
          </Heading>
          <Text>
            The Agreement shall be deemed terminated when termination has taken
            effect, provided that (a) the Data Transfer has been successfully
            completed; or (b) upon expiry of the Notice Period, if Customer
            wishes to delete the Transferable Data in accordance with section
            4.2 (c) above. Customer will be notified in writing of the
            termination of the Agreement. The Data Transfer is considered to
            have been successfully completed when Customer so informs Merkatus,
            Merkatus has verifiably completed it, or, in any case, when the
            Retrieval Period has ended. Notwithstanding the aforesaid, access to
            the services shall cease in accordance with the Agreement.
          </Text>
        </Box>

        {/* Section 9 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            9. Provision of Information
          </Heading>
          <Text>
            Merkatus publishes the information required under Article 28(1) of
            the Data Act at{' '}
            <Link
              href="https://merkatus.io/data-act"
              color="darkBlue.900"
              isExternal>
              https://merkatus.io/data-act
            </Link>
            . This includes the information required by the transparency
            obligation concerning international access and transfers relating to
            jurisdictions, as well as an overview of data security measures.
          </Text>
        </Box>

        {/* Section 10 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            10. Transfer Fees
          </Heading>
          <Text>Merkatus does not charge for Data Transfers.</Text>
        </Box>

        {/* Section 11 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            11. Contact
          </Heading>
          <Text>
            Merkatus.io Oy
            <br />
            Lapinlahdenkatu 16, 00180 Helsinki, Finland
            <br />
            <Link
              href="mailto:support@merkatus.io"
              color="darkBlue.900"
              fontWeight="semibold">
              support@merkatus.io
            </Link>
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};
